import { ApplicationConfig, inject, APP_INITIALIZER } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { provideToastr } from 'ngx-toastr';
import { LookupService } from '@shared/services';
import { provideLoader } from '@shared/services/ui/loader/provideLoader';
import { provideCore } from '@core/core';
import { Location } from '@angular/common';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';
import { ConfirmationService, MessageService } from 'primeng/api';
import { lastValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AuthService, LanguageService } from '@core/services';
import { DialogService } from 'primeng/dynamicdialog';
import { BsModalService } from 'ngx-bootstrap/modal';

const Noir = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{slate.50}',
      100: '{slate.100}',
      200: '{slate.200}',
      300: '{slate.300}',
      400: '{slate.400}',
      500: '{slate.500}',
      600: '{slate.600}',
      700: '{slate.700}',
      800: '{slate.800}',
      900: '{slate.900}',
      950: '{slate.950}',
    },
    colorScheme: {
      light: {
        primary: {
          color: '{slate.950}',
          inverseColor: '#ffffff',
          hoverColor: '{slate.900}',
          activeColor: '{slate.800}',
        },
        highlight: {
          background: '{slate.950}',
          focusBackground: '{slate.700}',
          color: '#ffffff',
          focusColor: '#ffffff',
        },
      },
      dark: {
        primary: {
          color: '{slate.50}',
          inverseColor: '{slate.950}',
          hoverColor: '{slate.100}',
          activeColor: '{slate.200}',
        },
        highlight: {
          background: 'rgba(250, 250, 250, .16)',
          focusBackground: 'rgba(250, 250, 250, .24)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)',
        },
      },
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const authService = inject(AuthService);
        const languageService = inject(LanguageService);
        const translateService = inject(TranslateService);
        return async () => {
          await authService.initialize();
          const defaultLang = +(localStorage.getItem('language') ?? '1');
          const shortName = languageService.getShortNameById(defaultLang) || 'en';
          translateService.setDefaultLang(shortName);
          languageService.setCurrent(defaultLang);
          return lastValueFrom(translateService.use(shortName));
        };
      },
      multi: true,
    },
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Noir,
        options: {
          darkModeSelector: 'html.dark',
        },
      },
      pt: {
        button: {
          root: ({ instance }: any) => ({
            style: (() => {
              if (instance.severity === 'contrast' && !instance.outlined && instance.disabled) {
                return {
                  opacity: '0.25',
                };
              }
              if (instance.severity === 'contrast' && instance.outlined && instance.disabled) {
                return {
                  opacity: '0.5',
                };
              }
              return undefined;
            })(),
          }),
        },
      },
      zIndex: {
        modal: 1100, // dialog, sidebar
        overlay: 1200, // dropdown, overlaypanel
        menu: 1000, // overlay menus
        tooltip: 1100, // tooltip
      },
    }),
    Location,
    ConfirmationService,
    MessageService,
    provideCore(),
    provideLoader(),
    provideToastr(),
    LookupService,
    DialogService,
    BsModalService,
  ],
};
